/* lib/adapters/versions.js — форма версий/снимков между боевым API и компонентами версионирования.
 * Кросс-каттинг: один VersionsBlock на 3 редактора (kind ∈ {ttk, card, menu}).
 *
 * Главное расхождение backend↔mock — в snapshot'ах. UI (SnapshotView/DiffView/VersionCompareModal)
 * читает поля снимка ПО ИМЕНАМ, совпадающим с currentSnapshot редакторов (см. их useMemo):
 *   • card : {name, category, default_unit, kcal_per_100g, protein_per_100g, fat_per_100g,
 *             carbs_per_100g, allergens, notes, current_price:{amount_rub, unit, supplier_name}}
 *   • ttk  : {title, output_g, storage_conditions, allergens, kbju_per_portion:{kcal,protein,fat,carbs},
 *             ingredient_rows:[{name_raw, target_label, amount_brutto_g, amount_netto_g}]}
 *   • menu : {name, average_check, target_food_cost_pct, sections:[{id,title}],
 *             dishes:[{title, section, food_cost_pct, in_stop_list, _deleted}]}
 *
 * Бэк (versioning/services.snapshot_*) держит ИНОЕ: decimal-строки, плоский current_price_* вместо
 * объекта, storage/allergens/kbju внутри draft_json/calculated_json (TTK), section_title_at_snapshot +
 * cost/selling вместо section/food_cost_pct (Menu). Этот адаптер их сводит. Decimal→Number — только там,
 * где компоненты форматируют числа (fmt.money/grams/percent, KbjuTable, % потерь).
 *
 * Флаги _card_deleted/_sub_recipe_deleted (TTK-строки) и _deleted (блюда меню) сериализатор уже
 * проставил — пробрасываем как есть; для TTKSnapshotView дополнительно сводим в плоский row.deleted. */

const num = (v) => (v == null || v === '' ? null : Number(v));
const round1 = (v) => (v == null ? null : Math.round(v * 10) / 10);

// ── TTK snapshot ──────────────────────────────────────────────────────
// storage_conditions/allergens живут в draft_json; kbju_per_portion — в calculated_json.
// Строка: target_label синтезируем из *_at_snapshot/name_raw; cold_loss_pct — derived (% холодных
// потерь, как в TTKSnapshotView); deleted — плоский флаг из _card_deleted/_sub_recipe_deleted.
function adaptTTKRow(r) {
  const b = num(r.amount_brutto_g);
  const n = num(r.amount_netto_g);
  const label = r.card_name_at_snapshot || r.sub_recipe_title_at_snapshot || r.name_raw || '';
  return {
    name_raw: r.name_raw || '',
    target_label: label,
    amount_brutto_g: b,
    amount_netto_g: n,
    cold_loss_pct: (b != null && n != null && b > 0) ? round1(((b - n) / b) * 100) : null,
    deleted: !!(r._card_deleted || r._sub_recipe_deleted),
    _card_deleted: !!r._card_deleted,
    _sub_recipe_deleted: !!r._sub_recipe_deleted,
  };
}

function adaptTTKSnapshot(s) {
  const snap = s || {};
  const draft = (snap.draft_json && typeof snap.draft_json === 'object') ? snap.draft_json : {};
  const calc = (snap.calculated_json && typeof snap.calculated_json === 'object') ? snap.calculated_json : {};
  const kbju = (calc.kbju_per_portion && typeof calc.kbju_per_portion === 'object') ? calc.kbju_per_portion : null;
  return {
    title: snap.title || '',
    output_g: num(snap.output_g),
    storage_conditions: typeof draft.storage_conditions === 'string' ? draft.storage_conditions : '',
    allergens: Array.isArray(draft.allergens) ? draft.allergens : [],
    kbju_per_portion: kbju ? {
      kcal: num(kbju.kcal), protein: num(kbju.protein), fat: num(kbju.fat), carbs: num(kbju.carbs),
    } : null,
    ingredient_rows: (snap.ingredient_rows || []).map(adaptTTKRow),
  };
}

// ── Card snapshot ─────────────────────────────────────────────────────
// Плоские current_price_* → объект current_price{amount_rub, unit, supplier_name}; КБЖУ decimal→Number.
function adaptCardSnapshot(s) {
  const snap = s || {};
  const amount = num(snap.current_price_amount_rub);
  return {
    name: snap.name || '',
    category: snap.category || '',
    default_unit: snap.default_unit || '',
    kcal_per_100g: num(snap.kcal_per_100g),
    protein_per_100g: num(snap.protein_per_100g),
    fat_per_100g: num(snap.fat_per_100g),
    carbs_per_100g: num(snap.carbs_per_100g),
    allergens: Array.isArray(snap.allergens) ? snap.allergens : [],
    notes: snap.notes || '',
    current_price: amount != null ? {
      amount_rub: amount,
      unit: snap.current_price_unit || '',
      supplier_name: snap.current_price_supplier || '',
    } : null,
  };
}

// ── Menu snapshot ─────────────────────────────────────────────────────
// dish: title_at_snapshot → title; section_title_at_snapshot → section; food_cost_pct бэк в снимке
// блюда НЕ хранит — выводим из cost/selling (как в каскаде стоимости), иначе null.
function adaptMenuDish(d) {
  const cost = num(d.cost_per_portion_rub);
  const sell = num(d.selling_price_rub);
  const fc = (cost != null && sell != null && sell > 0) ? round1((cost / sell) * 100) : null;
  return {
    title: d.title_at_snapshot || '',
    section: d.section_title_at_snapshot || '',
    food_cost_pct: fc,
    in_stop_list: !!d.in_stop_list,
    _deleted: !!d._deleted,
  };
}

function adaptMenuSnapshot(s) {
  const snap = s || {};
  return {
    name: snap.name || '',
    average_check: num(snap.average_check),
    target_food_cost_pct: num(snap.target_food_cost_pct),
    sections: (snap.sections || []).map((x) => ({ id: x.id, title: x.title || '' })),
    dishes: (snap.dishes || []).map(adaptMenuDish),
  };
}

const SNAPSHOT_ADAPTERS = {
  ttk: adaptTTKSnapshot,
  card: adaptCardSnapshot,
  menu: adaptMenuSnapshot,
};

export function adaptSnapshot(kind, snapshot) {
  const fn = SNAPSHOT_ADAPTERS[kind] || ((s) => s || {});
  return fn(snapshot);
}

// ── Версия (мета + snapshot) ──────────────────────────────────────────
// Форма как у мок-getVersions: {version_number, created_at, summary, author_email, snapshot}.
// Только detail-сериализатор отдаёт snapshot; list-мета — без него (snapshot=null до гидрации).
export function adaptVersion(kind, v) {
  if (!v) return null;
  return {
    id: v.id,
    version_number: v.version_number,
    summary: v.summary || '',
    author_email: v.author_email || null, // SET_NULL — может быть null, UI это терпит
    created_at: v.created_at,
    updated_at: v.updated_at,
    snapshot: v.snapshot ? adaptSnapshot(kind, v.snapshot) : null,
  };
}
